//#region ENUM NavigationPageEnum
/**
 * Enum representing the available pages within the application for navigation.
 *
 * Each page corresponds to a unique section or feature in the application.
 *
 * @enum {string}
 * @description This enum defines the available pages that a user can navigate to within the application.
 */
export enum NavigationPageEnum {
  AutoOrdering = 'auto-ordering',
  AutoOrderingGeneralSettings = 'auto-ordering-general-settings',
  AutoOrderingOrderSettings = 'auto-ordering-order-settings',
  AutoOrderingOpenOrderSettings = 'auto-ordering-open-order-settings',
  AutoOrderingOrderSchedulerSettings = 'auto-ordering-order-scheduler-settings',
  AutoOrderingDocumentLayouts = 'auto-ordering-document-layouts',
  // BarcodeScannerPageBinLocations = 'barcode-scanner/bin-locations',
  BarcodeScannerPageBinLocationsUpdate = 'barcode-scanner/bin-locations/update-bin-location',
  BarcodeScannerPageHome = 'barcode-scanner/home',
  // BarcodeScannerPageOrder = 'barcode-scanner/order',
  BarcodeScannerPageOrderCreate = 'barcode-scanner/order/create-order',
  BarcodeScannerPageProcessGRV = 'barcode-scanner/process-grv',
  // BarcodeScannerPageShelfTalkers = 'barcode-scanner/shelf-talkers',
  BarcodeScannerPageShelfTalkersPrint = 'barcode-scanner/shelf-talkers/print-shelf-talkers',
  // BarcodeScannerPageStock = 'barcode-scanner/stock',
  BarcodeScannerPageStockUpdate = 'barcode-scanner/stock/update-stock',
  CopySettings = 'copy-settings',
  Dashboard = 'dashboard',
  DashboardReporting = 'dashboard-reporting',
  Email = 'email',
  Home = 'home',
  LocalStorage = 'local-storage',
  LocalStorageManager = 'local-storage-manager',
  LabelMaker = 'label-maker',
  Login = 'login',
  NGPReport = 'ngp-report',
  Notifications = 'notifications',
  Preferences = 'Preferences',
  Quotes = 'quotes',
  Settings = 'settings-old',
  SettingsNew = 'settings',
  ShelfTalkers = 'shelf-talkers',
  ShelfTalkersGeneral = 'shelf-talkers-general',
  StockTake = 'stock-take',
  StockTakeGeneral = 'stock-take-general',
  ProductMapping = 'stock-match',
  StockManager = 'stock-manager',
  StockMaster = 'stock-master',
  StockRules = 'stock-rules',
  StockRulesItemRuleAdd = 'stock-rules-item-rule-add',
  StockRulesItemRuleEnable = 'stock-rules-item-rule-enable',
  StockRulesItemRuleDisable = 'stock-rules-item-rule-disable',
  StockRulesItemRules = 'stock-rules-item-rules',
  StockRulesAdvancedFilters = 'stock-rules-advanced-filters',
  StockTags = 'stock-tags',
  PriceThresholds = 'price-thresholds',
  PriceBanding = 'price-banding',
  MultiplePriceChanges = 'multiple-price-changes',
  Store = 'store',
  StoreDisplayOrder = 'store-display-order',
  TableSettings = 'table-settings',
  User = 'user',
  UserAccess = 'user-access',
  UserProfile = 'user-profile',
}

//#endregion

//#region ENUM NavigationPageEnumHumanReadable
export enum NavigationPageEnumHumanReadable {
  AutoOrdering = 'Auto Ordering',
  AutoOrderingGeneralSettings = 'General',
  AutoOrderingOrderSettings = 'Order',
  AutoOrderingOpenOrderSettings = 'Open Order Defaults',
  AutoOrderingOrderSchedulerSettings = 'Scheduler',
  AutoOrderingDocumentLayouts = 'Purchase Order Layout',
  // BarcodeScannerPageBinLocations = 'Bin Locations',
  BarcodeScannerPageBinLocationsUpdate = 'Update Bin Location',
  BarcodeScannerPage = 'Barcode Scanner',
  BarcodeScannerPageHome = 'Scanner Home',
  // BarcodeScannerPageOrder = 'Order',
  BarcodeScannerPageOrderCreate = 'Create Orders',
  BarcodeScannerPageProcessGRV = 'Process GRV',
  // BarcodeScannerPageShelfTalkers = 'Shelf Talkers',
  BarcodeScannerPageShelfTalkersPrint = 'Print Shelf Talkers',
  // BarcodeScannerPageStock = 'Stock',
  BarcodeScannerPageStockUpdate = 'Update Stock',
  CopySettings = 'Copy Settings',
  Dashboard = 'Dashboard',
  DashboardReporting = 'Dashboard Reporting',
  Email = 'Email',
  General = 'general',
  Home = 'Home',
  LocalStorage = 'Local Storage',
  LocalStorageManager = 'Storage Manager',
  LabelMaker = 'Label Maker',
  Login = 'Login',
  NGPReport = 'NGP Report',
  Notifications = 'Notifications',
  Preferences = 'Preferences',
  Quotes = 'Quotes',
  Settings = 'Settings (Legacy)',
  SettingsNew = 'Settings',
  ShelfTalkers = 'Shelf Talkers',
  StockTake = 'Stock Take',
  // Sub-feature labels render nested under their parent, so this reads as
  // "Stock Take > General", matching the Auto Ordering entry of the same name.
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  StockTakeGeneral = 'General',
  ProductMapping = 'Product Mapping',
  StockManager = 'Stock Manager',
  StockMaster = 'Stock Master',
  StockRules = 'Stock Settings',
  StockRulesItemRuleAdd = 'Add Stock Item',
  StockRulesItemRuleEnable = 'Enable Stock Item',
  StockRulesItemRuleDisable = 'Disable Stock Item',
  StockRulesItemRules = 'Stock Item Rules',
  StockRulesAdvancedFilters = 'Advanced Filters',
  StockTags = 'Stock Tags',
  PriceThresholds = 'Price Thresholds',
  PriceBanding = 'Price Banding',
  MultiplePriceChanges = 'Multiple Price Change Rules',
  Store = 'Store',
  StoreDisplayOrder = 'Store Display Order',
  TableSettings = 'Table Settings',
  User = 'User',
  UserAccess = 'Staff Access',
  UserDefaults = 'User Defaults',
  UserProfile = 'User Profile',
}

//#endregion

//#region TYPE NavigationPageType
/**
 * Type representing the values of the {@link NavigationPageEnum} enum.
 *
 * This type is used to strongly type values that match a page value from the {@link NavigationPageEnum} enum.
 *
 * @type {string}
 * @example
 * const page: NavigationPageType = 'dashboard';
 */
export type NavigationPageType = `${NavigationPageEnum}`;
//#endregion

//#region ENUM NavigationTabEnum
/**
 * Enum representing the available tabs within specific pages for navigation.
 *
 * Each tab corresponds to a specific subsection or feature within a page.
 *
 * @enum {string}
 * @description This enum defines the available tabs that a user can select within specific pages.
 */
export enum NavigationTabEnum {
  Email = 'email',
  LabelMaker = 'labelMaker', // Do not use for navigation
  LabelMakerTab = 'label-maker',
  OrderSettings = 'order-settings',
  OrderingSettings = 'ordering-settings',
  Orders = 'orders',
  Preview = 'preview',
  Schedule = 'schedule',
  Store = 'store',
  StockTakeDashboard = 'stock-take-dashboard',
  StockTakeList = 'stock-take-list',
  StockTakeSchedule = 'stock-take-schedule',
  StockManagerStock = 'stock',
  StockManagerMultiStore = 'multi-store',
  Unscheduled = 'unscheduled',
  User = 'user',
}

//#endregion

//#region TYPE NavigationTabType
/**
 * Type representing the values of the {@link NavigationTabEnum} enum.
 *
 * This type is used to strongly type values that match a tab value from the {@link NavigationTabEnum} enum.
 *
 * @type {string}
 * @example
 * const tab: NavigationTabType = 'email';
 */
export type NavigationTabType = `${NavigationTabEnum}`;
//#endregion
